import capitalize from 'lodash/capitalize';

import { formatDate } from '@/utils/commonFormats';
import { MachineTypeId } from '@/utils/machineType';

import { CreditState } from './CreditOverview';
import { Entitlements } from './Entitlements';
import type { SubscriptionAddOn } from './SubscriptionAddOn';

export const TEAMS = 'teams';
const TEAMS_FREE = 'teams-free';
const TEAMS_TRIAL = 'teams-trial';
export const STARTER_PACKAGE = 'teams-package';
const STARTER_PACKAGE_TRIAL = 'starter-package-trial';
export const STARTER_V2 = 'starter-package';
export const VELOCITY = 'velocity';
export const VELOCITY_TRIAL = 'velocity-trial';
export const ENTERPRISE = 'enterprise';
export const PRIVATE_CLOUD = 'private-cloud';
export const DEDICATED_CLOUD = 'dedicated-cloud';
const INACTIVE = 'inactive';
const TIME_BASED_TRIAL = 'time-based-trial';
export const PRO = 'pro';
export const PRO_TRIAL = 'pro-trial';
const HOBBY = 'deprecated-concrete-hobby';
export const BITRISE_PLATFORM = 'bitrise-platform';
export const BITRISE_PLATFORM_FREE = 'bitrise-platform-free';
export const CI_HOBBY = 'ci-hobby';

export const DEPRECATED_PAYING_PLANS = [
  'deprecated-concrete-indie',
  'deprecated-concrete-developer',
  'deprecated-concrete-pro',
  'deprecated-organization-elite',
  'deprecated-enterprise-elite',
] as const;
const DEPRECATED_LEGACY_PLANS = [...DEPRECATED_PAYING_PLANS, 'deprecated-concrete-hobby'] as const;

export type DeprecatedLegacyPlanType = (typeof DEPRECATED_LEGACY_PLANS)[number];

export const planAllowsPlusSupport = (plan: CreditPlan) => {
  return plan.name === TEAMS || plan.name === PRO || (DEPRECATED_PAYING_PLANS as readonly string[]).includes(plan.name);
};

const DEPRECATED_LEGACY_ELITE_PLANS: DeprecatedLegacyPlanType[] = [
  'deprecated-organization-elite',
  'deprecated-enterprise-elite',
];

export type KnownBillingPeriod = 'yearly' | 'monthly';
export type BillingPeriod = KnownBillingPeriod | string | null;

export type CreditPlan = {
  id: number;
  name: string;
  tier: string;
  creditAmount: number;
  totalPrice: number | null;
  paygLimit: number;
  ccLimitOsx?: number | null;
  ccLimitLinux?: number | null;
  ccLimit?: number | null;
  buildTimeoutInSeconds: number;
  publicAppNumberLimit: number;
  privateAppNumberLimit: number;
  billingPeriod: BillingPeriod;
  chargebeeId: string;
  renewable: boolean;
  storage?: number | null;
  storageUnit?: string | null;
  userLimit?: number | null;
  displayName?: string;
  deprecated?: boolean;
  entitlements: Entitlements;
  buildCountBased: boolean;
  buildCountAmount: number | null;
  macosMinutesAmount: number;
  linuxMinutesAmount: number;
  buildMinutesAmount?: number;
};
export function isStarterV2(plan?: CreditPlan) {
  return plan?.name === STARTER_V2;
}
export function isPro(plan?: CreditPlan): plan is CreditPlan & { buildCountAmount: number } {
  return plan?.name === PRO;
}
export const currentPlanIsActive = (plan: CreditPlan) =>
  plan.name !== 'inactive' &&
  plan.name !== 'restricted' &&
  plan.name !== BITRISE_PLATFORM &&
  plan.name !== BITRISE_PLATFORM_FREE;
export function isHobby(plan?: CreditPlan) {
  return plan?.name === HOBBY;
}
export function isDeprecatedLegacy(plan?: CreditPlan) {
  return plan && (DEPRECATED_LEGACY_PLANS as ReadonlyArray<string>).includes(plan.name);
}
type CreditProductSubscription = {
  productId: string;
  unitPrice: number;
  unitCount: number;
};

export type CreditSubscriptionData = {
  id: string;
  dataId: number;
  chargebeeSubscriptionId: string;
  creditsPerMonth: number;
  currentCredits: number;
  buildCountPerMonth: number;
  currentBuildCount: number;
  currentMacosMinutes: number;
  macosMinutesPerMonth: number;
  currentLinuxMinutes: number;
  linuxMinutesPerMonth: number;
  currentBuildMinutes?: number;
  currentMacosOveruseMinutes?: number;
  currentLinuxOveruseMinutes?: number;
  unifiedMinutesActive?: boolean;

  currentAiCredits?: number | null;
  aiCreditsPerMonth?: number | null;

  paygLimit: number;
  bufferLimit: number;
  cutoffLimit: number;
  supportPriority?: string;
  ccLimitOsx?: number | null;
  ccLimitLinux?: number | null;
  ccLimit?: number | null;
  buildTimeoutInSeconds: number;
  startedAt: string;
  currentPeriodStartedAt?: string;
  currentBillingPeriodStartedAt?: string;
  nextRenewalAt?: string;
  nextBillingAt?: string;
  cancelAt?: string;
  creditStatus: CreditState;
  availableBalance: number;
  overusage: number;
  currentPlan: CreditPlan;
  scheduledPlan?: CreditPlan;
  exceededScheduledLimits?: ExceededLimits;
  paygAvailable: boolean;
  wireTransfer: boolean;
  originalSubscription?: CreditSubscriptionData;
  products: CreditProductSubscription[];
  discountAmount?: number;
  trialEndsAt?: string | null;
  addOns?: SubscriptionAddOn[];
  remainingPaidProducts?: string[];
  canSelfServe: boolean;
  portalAvailable?: boolean;
  quantity?: number;
  quantizedPrice?: number;
  scheduledLimits?: EntitlementLimits;
  exceededLimits?: ExceededLimits;
  currentLimits?: EntitlementLimits;
  entitlements: Entitlements;
  buildCountBased: boolean;
  totalPrice?: number;
  topUps: number | null;
  updatedAt?: number | null;
  addOnsUpdatedAt?: number | null;
  lastTrialEndedAt: string | null;
  extraBuildCapacity?: {
    totalBuildCount: number;
    availableBuildCountInMonth: number;
    totalMacosMinutes: number;
    availableMacosMinutesInMonth: number;
    totalLinuxMinutes: number;
    availableLinuxMinutesInMonth: number;
  };
};

export type CreditChargesPreview = {
  paygFee: number;
  subscriptionFee: number;
  nextBillingFee: number;
  subscriptionUnitPrice?: number;
  subscriptionQuantity?: number;
  discountAmount: number;
  total: number;
  addOns?: Record<string, { amount: number; quantity?: number }>[];
  paygDetails: {
    creditCost?: number;
    creditUsage?: number;
    buildCountCost?: number;
    buildCountUsage?: number;
    macosMinutesCost?: number;
    macosMinutesUsage?: number;
    linuxMinutesCost?: number;
    linuxMinutesUsage?: number;
  };
  // RDE overage is billed separately from CI's, so it arrives as its own fee and per-platform breakdown.
  rdePaygFee?: number;
  rdePaygDetails?: Partial<Record<RdePaygPlatform, { minutes: number; cost: number }>>;
};

export const RDE_PAYG_PLATFORMS = ['macos', 'linux'] as const;

type RdePaygPlatform = (typeof RDE_PAYG_PLATFORMS)[number];

type CreditSubscriptionRenewState = 'trialing' | 'renewing' | 'cancelled' | 'non-renewing';

export type CreditSubscription = {
  chargebeeSubscriptionId: string;

  displayNameForPlanAndTier: { plan?: string; tier?: string };

  displayNameForScheduledPlanAndTier: { plan?: string; tier?: string };

  fullDisplayNameForPlanWithTier: string;
  nextBillingAt: string | undefined;
  nextRenewalAt: string | undefined;
  cancelAt: string | undefined;
  cancelAtDate: Date | undefined;
  startedAt: string | undefined;
  originalStartedAt: string | undefined;
  currentPeriodStartedAt: string | undefined;
  currentBillingPeriodStartedAt: string | undefined;
  currentAiCredits: number | null;
  aiCreditsPerMonth: number | null;
  slug: string;
  dataId: number;
  ccLimitOsx?: number | null;
  ccLimitLinux?: number | null;
  ccLimit?: number | null;
  currentCredits: number;
  creditsPerMonth: number;
  currentBuildCount: number;
  buildCountPerMonth: number | null;
  currentMacosMinutes: number;
  currentLinuxMinutes: number;
  currentBuildMinutes?: number;
  currentMacosOveruseMinutes?: number;
  currentLinuxOveruseMinutes?: number;
  unifiedMinutesActive?: boolean;
  creditStatus: CreditState;
  currentPlan: CreditPlan;
  availableBalance: number;
  paygLimit: number;
  overusage: number;
  billingPeriod: BillingPeriod;
  paygAvailable: boolean;
  onTeamsTrial: boolean;
  onInactive: boolean;
  onRestricted: boolean;
  onBitrisePlatform: boolean;
  onTeams: boolean;
  onPro: boolean;
  onReleaseManagement: boolean;
  onProTrial: boolean;
  onMonthlyPlan: boolean;
  onTeamsFree: boolean;
  onStarterV2: boolean;
  onVelocityOrEnterprise: boolean;
  onVelocityTrial: boolean;
  onVelocity: boolean;
  onHobby: boolean;
  onPrivateOrDedicatedCloud: boolean;
  onTimeBasedTrial: boolean;
  onBrowserStack: boolean;
  hasScheduledPlan: boolean;
  scheduledPrice: number | undefined | null;
  currentPrice: number | null;
  renewalState: CreditSubscriptionRenewState;
  originalSubscription: CreditSubscription | undefined;
  wireTransfer: boolean;
  products: CreditProductSubscription[];
  discountAmount: number | undefined;
  onStarterPackage: boolean;
  onStarterPackageTrial: boolean;
  trialEndsAt?: string | null;
  addOns?: SubscriptionAddOn[];
  remainingPaidProducts: string[];
  canSelfServe: boolean;
  onEnterprise: boolean;
  onDeprecatedLegacyPlan: boolean;
  onLegacyElitePlan: boolean;
  onMobileDevopsPlatform: boolean;
  onBuildCachePlatform: boolean;
  onBuildPlatform: boolean;
  quantity?: number;
  quantizedPrice?: number;
  exceededScheduledLimits?: ExceededLimits;
  scheduledLimits?: EntitlementLimits;
  scheduledPeriod?: BillingPeriod;
  exceededLimits?: ExceededLimits;
  currentLimits?: EntitlementLimits;
  scheduledPlanName?: string;
  scheduledPlanRemoved?: boolean;
  entitlements: Entitlements;
  buildCountBased: boolean;

  topUps: number;
  updatedAt?: number | null;
  extraBuildCapacity?: ExtraBuildCapacityInfo;
  extraBuildCapacityUsage: 'none' | ExtraBuildCapacityUsageInfo;
};

type ExtraBuildCapacityInfo = {
  [k in 'builds' | 'macosMinutes' | 'linuxMinutes']: {
    availableInCycle: number;
    totalPuchased: number;
  };
};

export type ExtraBuildCapacityUsageInfo = {
  since?: string;
  topUps: number;
  builds: { used: number; total: number };
  macosMinutes: { used: number; total: number };
  linuxMinutes: { used: number; total: number };
};

const ebcMetricUsage = ({
  total,
  availableInCycle,
  current,
  planLimit,
}: {
  total: number;
  availableInCycle: number;
  current: number;
  planLimit: number;
}) => ({
  used: total - availableInCycle + Math.min(Math.max(0, current - planLimit), availableInCycle),
  total,
});

export const extraBuildCapacityUsage = (data: CreditSubscriptionData): 'none' | ExtraBuildCapacityUsageInfo => {
  const ebc = data.extraBuildCapacity;
  if (!ebc) return 'none';
  if (ebc.totalBuildCount + ebc.totalMacosMinutes + ebc.totalLinuxMinutes === 0) return 'none';
  return {
    since: data.currentPeriodStartedAt,
    topUps: data.topUps || 0,
    builds: ebcMetricUsage({
      total: ebc.totalBuildCount,
      availableInCycle: ebc.availableBuildCountInMonth,
      current: data.currentBuildCount,
      planLimit: data.currentPlan.buildCountAmount || 0,
    }),
    macosMinutes: ebcMetricUsage({
      total: ebc.totalMacosMinutes,
      availableInCycle: ebc.availableMacosMinutesInMonth,
      current: data.currentMacosMinutes,
      planLimit: data.currentPlan.macosMinutesAmount || 0,
    }),
    linuxMinutes: ebcMetricUsage({
      total: ebc.totalLinuxMinutes,
      availableInCycle: ebc.availableLinuxMinutesInMonth,
      current: data.currentLinuxMinutes,
      planLimit: data.currentPlan.linuxMinutesAmount || 0,
    }),
  };
};

export type EntitlementLimits = {
  repositoryYml: boolean;
  concurrency?: number;
  privateApps?: number;
  uniqueSteps?: number;
  machineTypes?: { osx: MachineTypeId[]; linux: MachineTypeId[] };
  linuxConcurrency?: number | 'unlimited';
  osxConcurrency?: number | 'unlimited';
};

export function displayForPlan(creditPlan: Pick<CreditPlan, 'name' | 'tier'>): {
  plan?: string;
  tier?: string;
  full: string;
} {
  let { plan, tier, full } = displayForPlanOld(creditPlan);
  if (!full) {
    full = `${plan} ${capitalize(tier)}`;
  }
  return { plan, tier, full };
}
function displayForPlanOld(plan: Pick<CreditPlan, 'name' | 'tier'>): { plan?: string; tier?: string; full?: string } {
  const { name, tier } = plan;
  switch (name) {
    case TEAMS:
      return { plan: 'Teams', tier };
    case CI_HOBBY:
    case TEAMS_FREE:
    case BITRISE_PLATFORM_FREE:
      return { plan: 'Hobby' };
    case TEAMS_TRIAL:
      return { plan: 'Teams', tier: 'trial' };
    case TIME_BASED_TRIAL:
      return { plan: 'Teams', tier: 'trial' };
    case ENTERPRISE:
      return { plan: 'Enterprise', tier };
    case VELOCITY:
      return { plan: 'Velocity' };
    case PRIVATE_CLOUD:
      return { plan: 'Private Build Platform' };
    case DEDICATED_CLOUD:
      return { plan: 'Dedicated Build Platform' };
    case INACTIVE:
      return { plan: 'Inactive' };
    case STARTER_PACKAGE:
      return { plan: 'Starter Package', tier };
    case 'deprecated-concrete-developer':
      return { plan: 'Concrete Developer' };
    case 'deprecated-concrete-indie':
      return { plan: 'Concrete Indie' };
    case 'deprecated-concrete-pro':
      return { plan: 'Concrete Pro' };
    case 'deprecated-organization-elite':
      return { plan: 'Organization Elite' };
    case 'deprecated-enterprise-elite':
      return { plan: 'Enterprise Elite' };
    case STARTER_PACKAGE_TRIAL:
      return { plan: 'Starter Package', tier: 'trial' };
    case STARTER_V2:
      return { plan: 'Starter', tier: `Tier ${tier}` };
    case 'mobile-devops-platform':
      if (tier && tier !== 'custom') {
        return { plan: 'DevOps Platform', tier, full: `DevOps Platform (${capitalize(tier)})` };
      } else {
        return { plan: 'Mobile DevOps Platform' };
      }
    case 'mobile-devops-platform-aws':
      return { plan: 'Mobile DevOps Platform AWS' };
    case 'build-platform':
      return { plan: 'Build Platform' };
    case 'build-cache-platform':
      return { plan: 'Build Cache Platform' };
    case 'deprecated-concrete-hobby':
      return { plan: 'Hobby' };
    case PRO:
      return { plan: 'Pro', tier };
    case PRO_TRIAL:
      return { plan: 'Pro Trial' };
    case 'release-management':
      return { plan: 'Release Management' };
    case 'restricted':
      return { plan: 'Restricted' };
    default:
      return {};
  }
}

export function processSubscriptionData(data: CreditSubscriptionData): CreditSubscription {
  const displayNameForScheduledPlanAndTier = data.scheduledPlan ? displayForPlan(data.scheduledPlan) : {};
  const displayNameForPlanAndTier = displayForPlan(data.currentPlan);
  const onTeamsTrial = data.currentPlan.name === TEAMS_TRIAL;
  const onTimeBasedTrial = data.currentPlan.name === TIME_BASED_TRIAL;
  const onStarterPackageTrial = data.currentPlan.name === STARTER_PACKAGE_TRIAL;
  const onInactive = data.currentPlan.name === INACTIVE;
  const onStarterV2 = data.currentPlan.name === STARTER_V2;

  let renewalState: CreditSubscriptionRenewState = 'renewing';
  if (onInactive) {
    renewalState = 'non-renewing';
  }
  if (onTeamsTrial || onStarterPackageTrial || onTimeBasedTrial) {
    renewalState = 'trialing';
  }
  if (data.cancelAt) {
    renewalState = 'cancelled';
  }
  const quantizedPrice =
    data.currentPlan.totalPrice && typeof data.quantity === 'number'
      ? data.currentPlan.totalPrice * data.quantity
      : undefined;
  return {
    slug: data.id,
    dataId: data.dataId,
    aiCreditsPerMonth: data.aiCreditsPerMonth ?? null,
    currentAiCredits: data.currentAiCredits ?? null,
    chargebeeSubscriptionId: data.chargebeeSubscriptionId,
    displayNameForPlanAndTier,
    displayNameForScheduledPlanAndTier,
    fullDisplayNameForPlanWithTier: displayNameForPlanAndTier.full,
    nextBillingAt: data.nextBillingAt && formatDate(data.nextBillingAt),
    nextRenewalAt: data.nextRenewalAt && formatDate(data.nextRenewalAt),
    startedAt: formatDate(data.startedAt),
    originalStartedAt: data.startedAt,
    cancelAt: data.cancelAt && formatDate(data.cancelAt),
    cancelAtDate: data.cancelAt ? new Date(data.cancelAt) : undefined,
    creditsPerMonth: data.creditsPerMonth,
    currentCredits: data.currentCredits,
    buildCountPerMonth: data.buildCountPerMonth,
    currentBuildCount: data.currentBuildCount,
    currentMacosMinutes: data.currentMacosMinutes,
    currentLinuxMinutes: data.currentLinuxMinutes,
    currentBuildMinutes: data.currentBuildMinutes,
    currentMacosOveruseMinutes: data.currentMacosOveruseMinutes,
    currentLinuxOveruseMinutes: data.currentLinuxOveruseMinutes,
    unifiedMinutesActive: data.unifiedMinutesActive,
    creditStatus: data.creditStatus,
    currentPeriodStartedAt: data.currentPeriodStartedAt && formatDate(data.currentPeriodStartedAt),
    currentBillingPeriodStartedAt: data.currentBillingPeriodStartedAt && formatDate(data.currentBillingPeriodStartedAt),
    currentPlan: data.currentPlan,
    availableBalance: data.availableBalance,
    paygLimit: data.paygLimit,
    ccLimit: data.ccLimit,
    ccLimitOsx: data.ccLimitOsx,
    ccLimitLinux: data.ccLimitLinux,
    overusage: data.overusage,
    billingPeriod: data.currentPlan.billingPeriod,
    paygAvailable: data.paygAvailable,
    onTeamsTrial,
    onTimeBasedTrial,
    onBrowserStack: data.currentPlan.name === 'browserstack',
    onInactive,
    onRestricted: data.currentPlan.name === 'restricted',
    onBitrisePlatform: data.currentPlan.name === 'bitrise-platform',
    scheduledPlanRemoved: data.scheduledPlan?.name === 'bitrise-platform',
    renewalState,
    onTeams: data.currentPlan.name === TEAMS,
    onPro: data.currentPlan.name === PRO,
    onProTrial: data.currentPlan.name === PRO_TRIAL,
    onTeamsFree: data.currentPlan.name === TEAMS_FREE,
    onStarterPackage: data.currentPlan.name === STARTER_PACKAGE,
    onStarterPackageTrial,
    onVelocityOrEnterprise: [VELOCITY, ENTERPRISE].includes(data.currentPlan.name),
    onVelocity: data.currentPlan.name === VELOCITY,
    onVelocityTrial: data.currentPlan.name === VELOCITY_TRIAL,
    onPrivateOrDedicatedCloud: [PRIVATE_CLOUD, DEDICATED_CLOUD].includes(data.currentPlan.name),
    onEnterprise: data.currentPlan.name === ENTERPRISE,
    onReleaseManagement: data.currentPlan.name === 'release-management',
    onStarterV2,
    onMonthlyPlan: data.currentPlan.billingPeriod === 'monthly',
    hasScheduledPlan: !!data.scheduledPlan,
    scheduledPrice: data.scheduledPlan?.totalPrice && data.scheduledPlan.totalPrice,
    originalSubscription: data.originalSubscription && processSubscriptionData(data.originalSubscription),
    currentPrice: data.totalPrice || data.currentPlan.totalPrice,
    wireTransfer: data.wireTransfer,
    products: data.products,
    discountAmount: data.discountAmount,
    trialEndsAt: data.trialEndsAt,
    addOns: data.addOns,
    remainingPaidProducts: data.remainingPaidProducts ?? [],
    canSelfServe: data.canSelfServe,
    quantity: data.quantity,
    onDeprecatedLegacyPlan: (DEPRECATED_LEGACY_PLANS as readonly string[]).includes(data.currentPlan.name),
    onLegacyElitePlan: (DEPRECATED_LEGACY_ELITE_PLANS as string[]).includes(data.currentPlan.name),
    quantizedPrice,
    exceededScheduledLimits: data.exceededScheduledLimits,
    scheduledLimits: isStarterV2(data.scheduledPlan) ? data.scheduledLimits : undefined,
    scheduledPlanName: data.scheduledPlan?.displayName,
    scheduledPeriod: data.scheduledPlan?.billingPeriod,
    exceededLimits: data.exceededLimits,
    currentLimits: data.currentLimits,
    onMobileDevopsPlatform: ['mobile-devops-platform', 'mobile-devops-platform-aws'].includes(data.currentPlan.name),
    onBuildCachePlatform: data.currentPlan.name === 'build-cache-platform',
    onBuildPlatform: data.currentPlan.name === 'build-platform',
    onHobby: isHobby(data.currentPlan),
    entitlements: data.entitlements,
    buildCountBased: data.currentPlan.buildCountBased,
    topUps: data.topUps || 0,
    updatedAt: Math.max(data.updatedAt ?? 0, data.addOnsUpdatedAt ?? 0) || null,
    extraBuildCapacity: data.extraBuildCapacity
      ? {
          builds: {
            totalPuchased: data.extraBuildCapacity.totalBuildCount,
            availableInCycle: data.extraBuildCapacity.availableBuildCountInMonth,
          },
          macosMinutes: {
            totalPuchased: data.extraBuildCapacity.totalMacosMinutes,
            availableInCycle: data.extraBuildCapacity.availableMacosMinutesInMonth,
          },
          linuxMinutes: {
            totalPuchased: data.extraBuildCapacity.totalLinuxMinutes,
            availableInCycle: data.extraBuildCapacity.availableLinuxMinutesInMonth,
          },
        }
      : undefined,
    extraBuildCapacityUsage: extraBuildCapacityUsage(data),
  };
}

export type ChangeToFreePlanRequest = {
  checkMigrationRequired: boolean;
};

export function getConcurrencyTotalForPlan(plan: CreditPlan) {
  if (plan.ccLimit) {
    return plan.ccLimit;
  }
  return (plan.ccLimitLinux || 0) + (plan.ccLimitOsx || 0);
}

const modernConcurrencyPlans = [STARTER_PACKAGE, STARTER_PACKAGE_TRIAL, STARTER_V2];
export function isModernConcurrencyPlan(plan: CreditPlan) {
  return modernConcurrencyPlans.includes(plan.name);
}

export type ExceededLimits = {
  privateApps?: {
    count: number;
    limit: number;
  };
  owners?: {
    count: number;
    limit: number;
  };
  teamMembers?: {
    count: number;
    limit: number;
  };
  uniqueStepsExceeded?: number;
};
